import styled from '@emotion/styled'
import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'

import { useImageGalleryContext } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { LeftArrowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/LeftArrowIcon'
import { transientOptions } from '@f-fertighaus/applications/Next/utils/transientOptions'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

export const GALLERY_NAVBAR_HEIGHT = '56px' as const
export const GALLERY_NAVBAR_HEIGHT_WITH_TABS = '120px' as const

interface Props {
  title: string
  ctaButton?: React.ReactNode
  children: React.ReactNode
}

export const ImageGalleryNavigation = ({
  title,
  ctaButton,
  children
}: Props) => {
  const {
    state: { focusView },
    dispatch
  } = useImageGalleryContext()
  const handleCloseClick = () => {
    if (focusView.open) {
      webtrack({
        category: 'image',
        action: 'click',
        label: 'close-zoom',
        location: 'image-zoom',
        values: [
          {
            type: 'url',
            value:
              focusView?.imageSources?.[focusView.displayImgIndex].src || ''
          }
        ]
      })
      dispatch({ type: 'closeFocusView' })
    } else {
      webtrack({
        category: 'CTA-button',
        action: 'click',
        label: 'close-gallery',
        location: 'image-gallery',
        values: [{ type: 'text', value: '(ICON)' }]
      })
      dispatch({ type: 'closeGallery' })
    }
  }

  return (
    <Wrapper $darkMode={focusView.open}>
      <Content>
        <IconButton
          data-sel='btn-back'
          onClick={handleCloseClick}
          sx={{ width: GALLERY_NAVBAR_HEIGHT, height: GALLERY_NAVBAR_HEIGHT }}>
          <LeftArrowIcon />
        </IconButton>
        <Typography
          variant='subtitle1'
          component='p'
          color={focusView.open && 'background.paper'}>
          {title}
        </Typography>
        <Box
          sx={{
            width: GALLERY_NAVBAR_HEIGHT,
            height: GALLERY_NAVBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          {!focusView.open && ctaButton}
          {focusView.open &&
            focusView.imageSources &&
            focusView.imageSources.length > 1 && (
              <Typography
                variant='subtitle1'
                component='p'
                color='background.paper'>
                {`${focusView.displayImgIndex + 1}/${
                  focusView.imageSources?.length
                }`}
              </Typography>
            )}
        </Box>
      </Content>
      {!focusView.open && (
        <Content>
          <Box sx={{ width: '100%', marginTop: '8px' }}>{children}</Box>
        </Content>
      )}
    </Wrapper>
  )
}

const Wrapper = styled('div', transientOptions)<{ $darkMode?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
  background-color: ${({ theme, $darkMode }) =>
    $darkMode ? theme.palette.common.black : theme.palette.background.paper};
  width: 100%;
  box-shadow: ${({ $darkMode }) =>
    $darkMode
      ? 'none'
      : '1px 1px 0 #e9e9e9, 0 2px 8px rgba(59, 71, 104, 0.15)'};

  path {
    ${({ theme, $darkMode }) =>
      $darkMode && `stroke: ${theme.palette.background.paper}`}
  }
`
export const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1160px;
  margin: auto;
  box-sizing: content-box;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: 0 5vw;
  }
`
